import { useEffect, useState } from 'react';
import styled from 'styled-components';

import ModalBase from 'components/Modal/ModalBase';

import { TicketFilterInput } from './hooks/useBookingListHooks';
import { ProductFilterList } from 'containers/ProductList/graphQL/types';
import { IconClose } from 'assets/icons';

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 32px 20px;

  > h6 {
    margin: 0;

    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #1b1c1e;
  }

  > svg {
    width: 32px;
    height: 32px;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    padding: 24px 20px;

    > svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const ProductFilterListBox = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;

  list-style: none;

  padding: 12px 20px 64px;
  margin: 0;

  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 12px 20px 40px;
  }

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: space-between;

    padding: 16px 18px;

    border-radius: 12px;
    border: 1.5px solid;
    border-color: transparent;

    background-color: rgba(247, 247, 248, 1);

    cursor: pointer;

    &.selected {
      border: 1.5px solid;
      border-color: rgba(65, 75, 86, 1);
    }

    p {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;

      color: rgba(70, 71, 76, 1);

      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      width: 100%;
    }

    img {
      width: 44px;
      height: 44px;

      border-radius: 6px;

      object-fit: cover;
    }
  }
`;

const EmptyProductList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const FooterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  padding: 16px 20px;

  border-top: 1px solid;
  border-color: rgba(234, 235, 236, 1);
`;

const Button = styled.button`
  height: 48px;
  padding: 0 24px;

  border-radius: 12px;

  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;

const ApplyButton = styled(Button)`
  background-color: rgba(27, 28, 30, 1);
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
`;

const ResetButton = styled(Button)<{ $isActive: boolean }>`
  ${({ $isActive }) => `
    background-color: ${
      $isActive ? 'rgba(244, 244, 245, 1)' : 'rgba(244, 244, 245, 1)'
    };
    color: ${$isActive ? 'rgba(70, 71, 76, 1)' : 'rgba(219, 220, 223, 1)'};
    cursor: ${$isActive ? 'pointer' : 'not-allowed'};
  `}
`;

interface Props {
  isMobile: boolean;
  handleProductFilterModal: () => void;
  currentFilter: TicketFilterInput['filter'];
  productFilterList: ProductFilterList;
  updateFilter: (key: string, value: any) => void;
  // handleSelectingMonth: (value: Option) => void;
}

function ProductFilterModal({
  isMobile,
  handleProductFilterModal,
  currentFilter,
  updateFilter,
  productFilterList,
}: Props) {
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  const handleSelectProduct = (id: string) => {
    setSelectedProductId(id);
  };

  const handleApplyFilter = () => {
    updateFilter('filter', {
      ...currentFilter,
      productId: selectedProductId,
    });

    handleProductFilterModal();
  };

  const handleResetFilter = () => {
    setSelectedProductId(null);
  };

  useEffect(() => {
    setSelectedProductId(currentFilter.productId);
  }, [currentFilter]);

  return (
    <ModalBase
      isMobile={isMobile}
      handleModal={handleProductFilterModal}
      useCustomHeader
    >
      <ModalHeader>
        <h6>상품 필터</h6>

        <IconClose color="#70737C" onClick={handleProductFilterModal} />
      </ModalHeader>

      <ProductFilterListBox>
        {productFilterList.productContainer.products?.edges.length > 0 ? (
          productFilterList.productContainer.products?.edges.map(item => (
            <li
              key={item.node.id}
              onClick={() => handleSelectProduct(item.node.id)}
              className={selectedProductId === item.node.id ? 'selected' : ''}
            >
              <p>{item.node.title}</p>
              <img
                src={item.node.headerContents[0]?.content?.thumbnail}
                alt="thumbnail"
              />
            </li>
          ))
        ) : (
          <EmptyProductList>판매중인 상품이 없어요</EmptyProductList>
        )}
      </ProductFilterListBox>

      <FooterBox>
        <ResetButton
          onClick={handleResetFilter}
          $isActive={!!selectedProductId}
        >
          초기화
        </ResetButton>
        <ApplyButton onClick={handleApplyFilter}>적용</ApplyButton>
      </FooterBox>
    </ModalBase>
  );
}

export default ProductFilterModal;
